import { ISessionLog } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { dateProvider } from '@utils';
import { useMemo } from 'react';

import i18n from '@/i18n';
import { sdk } from '@/services';
import { formatSecondsInHoursAndMinutes } from '@/utils/formatSecondsInHoursAndMinutes';

const SessionLogService = sdk.getService('SessionLogService');

export const useAccessHistoryByUser = (userId: string) => {
  const { isLoading, data } = useQuery({
    queryKey: ['session-logs', 'access-history', userId],
    queryFn: () => SessionLogService.findAllByUser(userId)
  });

  const groupedAccessHistory = useMemo(() => {
    if (!data) return {};

    const sortedData = [...data].sort(
      (a, b) =>
        dateProvider.parse(b.createdAt).getTime() - dateProvider.parse(a.createdAt).getTime()
    );

    return sortedData.reduce((acc, log) => {
      const date = dateProvider.parse(log.createdAt);
      const key = `${dateProvider.monthName(date.getMonth(), i18n.language)} ${date.getFullYear()}`;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(log);
      return acc;
    }, {} as Record<string, ISessionLog[]>);
  }, [data]);

  const calcSessionDuration = (sessionLog: ISessionLog): string | undefined => {
    if (!sessionLog.closeConnectionAt) return;

    const totalSeconds = dateProvider.diff(
      sessionLog.createdAt,
      sessionLog.closeConnectionAt,
      'seconds'
    );

    const { hours, minutes } = formatSecondsInHoursAndMinutes(totalSeconds);

    if (hours > 0) {
      return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
    }

    return minutes > 0 ? `${minutes} min` : undefined;
  };

  return {
    accessHistory: data,
    groupedAccessHistory,
    isLoading,
    calcSessionDuration
  };
};
